import React, { useState } from 'react';
import type {
  UserManagementListingPermissionDetailsDto,
  UserManagementListingPermissionDto,
} from '@zg-rentals/rental-manager-env';
import {
  DatarouterError,
  formatAddress,
  HeadWrapper,
  LoadingConstants,
  LoadingStates,
  PermissionCheck,
  RentalManagerUserManagementCompanyWebApiHandler_getUmcListingPermissionDetail,
  USER_MANAGEMENT_PAGE,
  useRentalManagerUser,
  useRouter,
} from '@zg-rentals/rental-manager-env';
import { PageWrapper } from '@zg-rentals/rental-revenue-components';
import type { ColumnConfig } from '@zg-rentals/advanced-table';
import { AdvancedTable } from '@zg-rentals/advanced-table';
import { Alert, Anchor, Button, Icon, Spacer, Tag, Text, TextButton } from '@zillow/constellation-10';
import { IconChevronLeftOutline } from '@zillow/constellation-icons';
import { AddListingPermissionModal } from './AddListingPermissionModal';
import { RemoveListingPermissionModal } from './RemoveListingPermissionModal';

export const ManageListingPermissionsPage = () => {
  const [showAddPermissionModal, setShowAddPermissionModal] = useState(false);
  const [permissionToRemove, setPermissionToRemove] = useState<UserManagementListingPermissionDto | undefined>();
  const [listingPermissionDetails, setListingPermissionDetails] =
    React.useState<UserManagementListingPermissionDetailsDto>();
  const [loadingState, setLoadingState] = React.useState(LoadingStates.INITIAL);
  const router = useRouter();
  const { user } = useRentalManagerUser();
  const [errorMessage, setErrorMessage] = React.useState('');
  const { impersonatorEmail, impersonatorUserToken } = user;
  const isImpersonator = impersonatorEmail || impersonatorUserToken;

  const listingAlias = router.getRouteParam('listingAlias');

  const fetchPermissions = () => {
    if (!listingAlias) {
      return;
    }
    setLoadingState(LoadingStates.LOADING);
    RentalManagerUserManagementCompanyWebApiHandler_getUmcListingPermissionDetail({ listingAlias })
      .then((result) => {
        setListingPermissionDetails(result);
        setLoadingState(LoadingStates.SUCCESS);
        return;
      })
      .catch((error) => {
        setErrorMessage(DatarouterError.getDisplayMessage(error, 'Something went wrong, please try again.'));
        setLoadingState(LoadingStates.ERROR);
      });
  };

  React.useEffect(() => {
    fetchPermissions();
  }, [listingAlias]);

  const userRenderer = (staticPermission: UserManagementListingPermissionDto) => (
    <div>
      <Text textStyle="body-sm-bold">{`${staticPermission.email} `}</Text>
      {staticPermission.userToken === user.userToken && (
        <Tag tone="gray" size="sm" css={{ marginInlineEnd: 'xs' }}>
          You
        </Tag>
      )}
      <div>
        {!staticPermission.umcId && (
          <Tag tone="info" size="sm" css={{ marginInlineEnd: 'xs' }}>
            NOT IN A COMPANY
          </Tag>
        )}
        {staticPermission.umcId !== user.userManagementCompanyId && (
          <Tag tone="warning" size="sm" css={{ marginInlineEnd: 'xs' }}>
            NOT IN YOUR COMPANY
          </Tag>
        )}
        {staticPermission.umcId === user.userManagementCompanyId && (
          <Tag tone="success" size="sm" css={{ marginInlineEnd: 'xs' }}>
            IN YOUR COMPANY
          </Tag>
        )}
        {staticPermission.isAdmin && (
          <Tag tone="info" size="sm" css={{ marginInlineEnd: 'xs' }}>
            ADMIN
          </Tag>
        )}
      </div>
    </div>
  );

  const columnConfig: Array<ColumnConfig<UserManagementListingPermissionDto>> = [
    {
      width: 5,
      header: 'Email',
      dataKey: 'email',
      align: 'left',
      renderer: userRenderer,
    },
    {
      width: 2,
      header: 'Manage',
      align: 'right',
      renderer: (staticPermission: UserManagementListingPermissionDto) => {
        if (staticPermission.userToken === user.userToken) {
          return '-';
        }
        if (staticPermission.umcId === user.userManagementCompanyId && staticPermission.isAdmin) {
          return '-';
        }
        return <TextButton onClick={() => setPermissionToRemove(staticPermission)}>Remove</TextButton>;
      },
    },
  ];

  const showLoadingState = loadingState === LoadingConstants.LOADING || loadingState === LoadingConstants.INITIAL;

  const renderOverview = () => {
    if (showLoadingState || !listingPermissionDetails) return null;
    const addressParts = formatAddress(listingPermissionDetails.listing);
    return (
      <Spacer css={{ marginBlockEnd: 'md' }}>
        <Text textStyle="heading-sm">{addressParts.formattedAddress}</Text>
        <Spacer css={{ marginBlockStart: 'sm', textAlign: 'right' }}>
          <Button disabled={!!isImpersonator} onClick={() => setShowAddPermissionModal(true)}>
            Add new permission
          </Button>
        </Spacer>
      </Spacer>
    );
  };

  return (
    <PermissionCheck route={USER_MANAGEMENT_PAGE}>
      <PageWrapper
        title="Manage Listing Permissions"
        backButton={
          <Anchor>
            <TextButton asChild icon={<Icon render={<IconChevronLeftOutline />} />}>
              <a href="/rental-manager/properties">Back to properties</a>
            </TextButton>
          </Anchor>
        }
      >
        <AddListingPermissionModal
          shouldOpen={showAddPermissionModal && !!listingAlias}
          listingAlias={listingAlias || ''}
          onClose={(refresh: boolean) => {
            setShowAddPermissionModal(false);
            if (refresh) {
              fetchPermissions();
            }
          }}
        />
        {listingAlias && permissionToRemove && (
          <RemoveListingPermissionModal
            shouldOpen={!!permissionToRemove && !!listingAlias}
            listingAlias={listingAlias}
            permissionToRemove={permissionToRemove}
            onClose={(refresh: boolean) => {
              setPermissionToRemove(undefined);
              if (refresh) {
                fetchPermissions();
              }
            }}
          />
        )}
        <Spacer css={{ padding: 'sm' }}>
          <div className="ActivityHistoryPage">
            <HeadWrapper title="Manage User Management Listing Permissions" />
            {renderOverview()}
            {errorMessage && <Alert tone="critical">{errorMessage}</Alert>}
            <AdvancedTable
              columnConfig={columnConfig}
              data={listingPermissionDetails?.permissionDtos || []}
              isLoading={showLoadingState}
            />
          </div>
        </Spacer>
      </PageWrapper>
    </PermissionCheck>
  );
};

export default ManageListingPermissionsPage;
